/**
 * Utility to detect password-protected files (PDF, XLSX, DOCX)
 * before upload to prevent uploading files that cannot be processed.
 *
 * Detection strategies:
 * - PDF: pdfjs-dist - catches PasswordException when loading (web only)
 * - XLSX/DOCX: File signature check - encrypted Office files use OLE/CFB format
 *   (signature: D0 CF 11 E0) instead of ZIP format (signature: PK)
 *
 * This approach minimizes false positives by checking actual file structure.
 */
import getFileNameAndExtension from '../../../components/FileInput/utils/getFileNameAndExtension';
import { checkPdfProtection } from './checkPdfProtection';

export type ProtectionCheckResult = {
  isProtected: boolean;
  fileType: 'pdf' | 'xlsx' | 'docx' | 'unknown';
  error?: string;
};

const SUPPORTED_EXTENSIONS = ['pdf', 'xlsx', 'docx'] as const;

type SupportedExtension = (typeof SUPPORTED_EXTENSIONS)[number];

/**
 * Get file extension from filename (lowercase), without the leading dot
 */
function getFileExtension(fileName: string): string {
  const { extension } = getFileNameAndExtension(fileName);
  return extension?.toLowerCase() ?? '';
}

/**
 * Check if a file extension is one we can check for protection
 */
export function isSupportedForProtectionCheck(fileName: string): boolean {
  const ext = getFileExtension(fileName);
  return ext !== '' && SUPPORTED_EXTENSIONS.includes(ext as SupportedExtension);
}

// OLE/CFB file signature (D0 CF 11 E0 A1 B1 1A E1)
// Encrypted Office documents (XLSX, DOCX, etc.) are wrapped in CFB containers
const OLE_SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0];

// ZIP file signature (PK)
const ZIP_SIGNATURE = [0x50, 0x4b];

/**
 * Check if the file starts with OLE/CFB signature
 * Encrypted Office documents use this format
 */
function hasOleSignature(uint8Array: Uint8Array): boolean {
  return (
    uint8Array[0] === OLE_SIGNATURE[0] &&
    uint8Array[1] === OLE_SIGNATURE[1] &&
    uint8Array[2] === OLE_SIGNATURE[2] &&
    uint8Array[3] === OLE_SIGNATURE[3]
  );
}

/**
 * Check if the file starts with ZIP signature (normal OOXML format)
 */
function hasZipSignature(uint8Array: Uint8Array): boolean {
  return uint8Array[0] === ZIP_SIGNATURE[0] && uint8Array[1] === ZIP_SIGNATURE[1];
}

/**
 * Check if an XLSX file is password-protected
 *
 * Detection strategy:
 * 1. Check file signature - encrypted XLSX files are OLE/CFB format, not ZIP
 * 2. Normal XLSX files are ZIP format (start with "PK")
 * 3. If it's OLE format but has .xlsx extension, it's encrypted
 */
function checkXlsxProtection(arrayBuffer: ArrayBuffer): ProtectionCheckResult {
  try {
    const uint8Array = new Uint8Array(arrayBuffer);

    // Encrypted XLSX files are wrapped in OLE/CFB container format
    // instead of being a regular ZIP file
    if (hasOleSignature(uint8Array)) {
      return {
        isProtected: true,
        fileType: 'xlsx',
        error: 'Excel file is password-protected',
      };
    }

    // Normal XLSX files are ZIP format (start with "PK")
    if (hasZipSignature(uint8Array)) {
      // It's a valid ZIP, so it's not encrypted (at file level)
      // Note: Worksheet/workbook protection without encryption is NOT detected here
      // as those files can still be opened and uploaded
      return { isProtected: false, fileType: 'xlsx' };
    }

    // Unknown format - not a valid XLSX
    // Don't flag as protected to avoid false positives
    return { isProtected: false, fileType: 'xlsx' };
  } catch {
    return { isProtected: false, fileType: 'xlsx' };
  }
}

/**
 * Check if a DOCX file is password-protected
 *
 * Detection strategy:
 * 1. Check file signature - encrypted DOCX files are OLE/CFB format, not ZIP
 * 2. Normal DOCX files are ZIP format (start with "PK")
 */
function checkDocxProtection(arrayBuffer: ArrayBuffer): ProtectionCheckResult {
  try {
    const uint8Array = new Uint8Array(arrayBuffer);

    // Encrypted DOCX files are wrapped in OLE/CFB container format
    if (hasOleSignature(uint8Array)) {
      return {
        isProtected: true,
        fileType: 'docx',
        error: 'Word document is password-protected',
      };
    }

    // Normal DOCX files are ZIP format
    if (hasZipSignature(uint8Array)) {
      return { isProtected: false, fileType: 'docx' };
    }

    // Unknown format - don't flag as protected to avoid false positives
    return { isProtected: false, fileType: 'docx' };
  } catch {
    return { isProtected: false, fileType: 'docx' };
  }
}

/**
 * Check if a file is password-protected
 *
 * Supports: PDF, XLSX, DOCX
 *
 * @param file - File or Blob to check
 * @param fileName - Name of the file (used to determine type by extension)
 * @returns Promise resolving to protection check result
 */
export async function checkFileProtection(
  file: File | Blob,
  fileName?: string,
): Promise<ProtectionCheckResult> {
  const name = fileName ?? (file instanceof File ? file.name : '');
  const ext = getFileExtension(name);

  if (!SUPPORTED_EXTENSIONS.includes(ext as SupportedExtension)) {
    return { isProtected: false, fileType: 'unknown' };
  }

  try {
    const arrayBuffer = await file.arrayBuffer();

    switch (ext) {
      case 'pdf':
        return await checkPdfProtection(arrayBuffer);
      case 'xlsx':
        return checkXlsxProtection(arrayBuffer);
      case 'docx':
        return checkDocxProtection(arrayBuffer);
      default:
        return { isProtected: false, fileType: 'unknown' };
    }
  } catch {
    // If we can't read the file, don't block upload
    return { isProtected: false, fileType: 'unknown' };
  }
}

/**
 * Check multiple files for password protection
 *
 * @param files - Array of files to check
 * @returns Promise resolving to array of results with file references
 */
export async function checkFilesProtection<T extends File | Blob>(
  files: T[],
  getFileName?: (file: T) => string,
): Promise<{ file: T; result: ProtectionCheckResult }[]> {
  const results = await Promise.all(
    files.map(async (file) => {
      let fileName: string;
      if (getFileName) {
        fileName = getFileName(file);
      } else if (file instanceof File) {
        fileName = file.name;
      } else {
        fileName = '';
      }

      // Skip files that don't need checking
      if (!isSupportedForProtectionCheck(fileName)) {
        return { file, result: { isProtected: false, fileType: 'unknown' as const } };
      }

      const result = await checkFileProtection(file, fileName);
      return { file, result };
    }),
  );

  return results;
}
